'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.js';
import { ActionCardFooter } from '@sola/ark-core/molecules/action-card/action-card.js';
import {
  Dialog,
  DialogActionClose,
  DialogActionPrimary,
  DialogActions,
  DialogContent,
  DialogTrigger,
} from '@sola/ark-core/molecules/dialog/dialog.js';
import { SupporterErrorTypeEnum } from '@sola/schema/dotcom/supporters/errors.js';
import {
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.js';
import { usePaymentMethodRemove } from '@sola/supporter-sdk/hooks.js';

import { PaymentMethodUpdateSheet } from '../../organisms/payment-method-update-sheet/payment-method-update-sheet';
import {
  createPaymentMethodSuccessAlert,
  createSupportAlert,
} from '../../utils/alerts';
import { getErrorTranslationKeys } from '../../utils/errors';
import { useLocalAlerts } from '../local-alerts/local-alerts-provider';

export const DialogRemovePaymentMethod: React.FC<{
  handleRemove: () => void;
  disableButton: boolean;
  openRemoveDialog: boolean;
  setOpenRemoveDialog: (open: boolean) => void;
}> = ({
  handleRemove,
  disableButton = false,
  openRemoveDialog,
  setOpenRemoveDialog,
}) => {
  const { t } = useTranslation('my');
  return (
    <Dialog open={openRemoveDialog} onOpenChange={setOpenRemoveDialog}>
      <DialogTrigger>
        <LinkStyle noUnderline noPadding size="lg" purpose="negative">
          <button
            onClick={() => setOpenRemoveDialog(true)}
            disabled={disableButton}
            type="button"
          >
            {t('payments.removeMethod')}
          </button>
        </LinkStyle>
      </DialogTrigger>
      <DialogContent
        size="sm"
        icon="info-circle"
        title={t('payments.removeMethodDialog.title')}
        supportingText={t('payments.removeMethodDialog.supportingText')}
        purpose="negative"
      >
        <DialogActions>
          <DialogActionClose>
            {t('payments.removeMethodDialog.cancel')}
          </DialogActionClose>
          <DialogActionPrimary
            purpose="negative"
            isBusy={disableButton}
            onClick={handleRemove}
          >
            {t('payments.removeMethodDialog.remove')}
          </DialogActionPrimary>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export type PaymentMethodCardFooterProps = {
  paymentMethod: GraphQLSupporterPaymentMethod;
  recurringCommitments?: GraphQLCommitment[];
  onRemoveSuccess?: () => void;
  onRemoveError?: () => void;
};

export const PaymentMethodCardFooter: React.FC<
  PaymentMethodCardFooterProps
> = ({ paymentMethod, recurringCommitments = [] }) => {
  const { t } = useTranslation('my');
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const { mutateAsync, isPending } = usePaymentMethodRemove(
    paymentMethod?.id || undefined,
  );
  const { addAlerts, clearAlerts } = useLocalAlerts();

  const handleRemove = async () => {
    if (!paymentMethod.id) {
      return;
    }

    clearAlerts();
    try {
      await mutateAsync();
      addAlerts(
        createPaymentMethodSuccessAlert(
          paymentMethod,
          'deletePaymentMethod.success.action',
        ),
      );
    } catch (errors) {
      const translationKeys = getErrorTranslationKeys(
        SupporterErrorTypeEnum.PaymentMethodDelete,
        errors,
      );
      addAlerts(
        translationKeys.map((translationKey) => {
          return createSupportAlert(translationKey);
        }),
      );
    }
    setOpenRemoveDialog(false);
  };

  const handleSetPrimary = () => null;
  const isShowingRemoveBtn =
    !recurringCommitments?.length && !paymentMethod.isPrimary;

  return (
    <ActionCardFooter justify="end">
      <div className="flex justify-end items-center gap-comfy-sm md:gap-comfy-md">
        {!paymentMethod.isPrimary && (
          <LinkStyle noUnderline noPadding size="lg" purpose="neutral">
            <button
              onClick={handleSetPrimary}
              disabled={isPending}
              type="button"
            >
              {t('payments.setPrimary')}
            </button>
          </LinkStyle>
        )}

        <div className="flex gap-comfy-md ml-auto">
          {isShowingRemoveBtn && (
            <DialogRemovePaymentMethod
              handleRemove={handleRemove}
              disableButton={isPending}
              openRemoveDialog={openRemoveDialog}
              setOpenRemoveDialog={setOpenRemoveDialog}
            />
          )}

          <PaymentMethodUpdateSheet
            openTrigger={
              <LinkStyle noUnderline noPadding size="lg">
                <button disabled={isPending} type="button">
                  {t('payments.editMethod')}
                </button>
              </LinkStyle>
            }
            paymentMethod={paymentMethod}
          />
        </div>
      </div>
    </ActionCardFooter>
  );
};
